import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BLOG_POSTS } from "@/data/blog";
import { ArrowUpRight, Clock } from "lucide-react";

export default function BlogTeaser() {
  const featured = [...BLOG_POSTS].reverse().slice(0, 3);
  return (
    <section
      id="journal"
      data-testid="blog-teaser-section"
      className="py-24 md:py-32 bg-cream-100"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Head */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase text-gold-dark mb-4 font-medium">
              — The Vardaan Journal
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink max-w-2xl leading-[1.05]">
              Stories of stone,
              <br />
              <span className="italic gold-text">craft and devotion.</span>
            </h2>
          </div>
          <Link
            to="/blog"
            data-testid="view-all-articles"
            className="group inline-flex items-center gap-3 px-6 py-3 border border-gold text-ink text-[11px] tracking-[0.28em] uppercase hover:bg-gold/10 transition-colors self-start"
          >
            Read All Articles
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </div>

        {/* 3-column article cards */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {featured.map((post, i) => (
            <motion.div
              key={post.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.7,
                delay: i * 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <Link
                to={`/blog/${post.slug}`}
                data-testid={`teaser-post-${post.slug}`}
                className="group block bg-cream-200/30 border border-gold/25 hover:border-gold transition-colors duration-500 overflow-hidden h-full"
              >
                <div className="relative overflow-hidden aspect-[16/10] bg-cream-200">
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                  {post.kicker && (
                    <div className="absolute top-4 left-4 bg-cream-100 border border-gold/40 px-3 py-1.5">
                      <div className="text-[10px] tracking-[0.28em] uppercase text-gold-dark font-medium">
                        {post.kicker}
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-6 md:p-7">
                  <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-ink-soft mb-3">
                    <Clock className="w-3 h-3" />
                    {post.readTime}
                  </div>
                  <h3 className="font-serif text-2xl md:text-[26px] text-ink leading-tight group-hover:text-gold-dark transition-colors duration-300">
                    {post.title}
                  </h3>
                  <p className="text-ink-soft mt-3 text-[15px] leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="mt-5 flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-gold-dark">
                    Read Article
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
